import * as sanitizeHtml_ from 'sanitize-html';

const sanitizeHtmlFn = sanitizeHtml_;

const sanitizeOptions = {
    allowedTags: [
        'h2',
        'h3',
        'h4',
        'h5',
        'h6',
        'blockquote',
        'p',
        'a',
        'ul',
        'ol',
        'nl',
        'li',
        'b',
        'i',
        's',
        'u',
        'strong',
        'em',
        'strike',
        'code',
        'hr',
        'br',
        'div',
        'span',
        'table',
        'thead',
        'caption',
        'tbody',
        'tr',
        'th',
        'td',
        'pre',
        'img',
        'label'
    ],
    allowedAttributes: {
        a: ['href', 'name', 'target', 'data-action', 'data-action-value'],
        img: ['src'],
        '*': ['class', 'style'],
        ol: ['start', 'type', 'square']
    },
    selfClosing: ['img', 'br', 'hr', 'area', 'base', 'basefont', 'input', 'link', 'meta'],
    allowedSchemes: ['http', 'https', 'data', 'mailto'],
    allowedSchemesByTag: {},
    allowedSchemesAppliedToAttributes: ['href', 'src', 'cite'],
    allowProtocolRelative: false,
    allowedIframeHostnames: <never>[]
};

/**
 * Strips insecure tags and attributes from the given html string.
 * When in a template, don't use this directly, use the `aixSanitize` pipe instead.
 */
export const sanitizeHtml = (html: string) => sanitizeHtmlFn(html, sanitizeOptions);

/**
 * True when the html brings its own presentation -- a full document, or a fragment carrying a
 * `<style>` block. `sanitizeHtml` drops both (`style`, `head` and `html` are not on its allowlist),
 * so content like this renders unstyled through the `aixSanitize` pipe. It has to go in an isolated
 * document instead, which is what `sanitizeDocument` prepares.
 */
export const isStyledDocument = (html: string | null | undefined) =>
    /<(?:html|head|style)[\s>]/i.test(html ?? '');

/**
 * Elements with no business in author-supplied content. `base` is here because the embed below sets
 * its own, and `form`/`noscript` because neither can do anything useful inside the sandbox.
 *
 * `meta` is here for `http-equiv="refresh"`: no sandbox flag suppresses a same-frame meta refresh,
 * so leaving it in would let a message replace itself with any remote page, inside a modal the user
 * cannot dismiss. Nothing an authored document puts in a `meta` -- charset, viewport -- means
 * anything inside `srcdoc`, so the whole element goes rather than just the one attribute.
 */
const unsafeElements = [
    'script',
    'iframe',
    'frame',
    'frameset',
    'object',
    'embed',
    'applet',
    'form',
    'noscript',
    'base',
    'meta'
];

/** Attributes whose value is a URL, and so needs its scheme checked. */
const urlAttributes = [
    'href',
    'src',
    'srcset',
    'xlink:href',
    'action',
    'formaction',
    'cite',
    'data'
];

const safeSchemes = ['http', 'https', 'mailto'];

/** `data:` is only safe where it cannot be navigated to -- an image, never a link. */
const dataUriAttributes = ['src', 'srcset'];

const isSafeUrl = (value: string, attribute: string) => {
    // Browsers drop control characters and whitespace while resolving a scheme, so `java\nscript:`
    // still reaches `javascript:`. Take them out before looking at it.
    // eslint-disable-next-line no-control-regex
    const scheme = /^([a-z0-9.+-]+):/.exec(value.replace(/[\u0000-\u0020]/g, '').toLowerCase());
    if (!scheme) {
        // Relative, protocol-relative or fragment -- no scheme to smuggle anything through.
        return true;
    }
    const name = scheme[1];
    return safeSchemes.includes(name) || (name === 'data' && dataUriAttributes.includes(attribute));
};

/**
 * Neutralises the standalone-page chrome an authored document carries so it sits flush inside the
 * frame: such documents are written to be opened on their own, so they centre themselves against a
 * full-viewport overlay background and cap their own width. The host modal already supplies all of
 * that. Everything else the author wrote -- their type scale, colours, panels -- is left alone.
 */
const embedReset = `
html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: transparent !important;
    display: block !important;
    width: auto !important;
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
}
body > * {
    margin: 0 !important;
    width: auto !important;
    max-width: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
}
`;

/**
 * Prepares author-supplied html for an isolated `<iframe srcdoc>`, keeping its own `<style>` intact
 * -- which is the whole point, and the one thing `sanitizeHtml` cannot do, since inlining a
 * stylesheet written for a standalone page would have it redecorate the app around it.
 *
 * Strips script-bearing elements, inline event handlers and unsafe url schemes. The frame is also
 * sandboxed without `allow-scripts`, so nothing in here executes even if something slips the net;
 * this pass is the second line, not the only one.
 *
 * That second line is what makes the round trip below acceptable: the result is serialised and then
 * re-parsed by the browser out of `srcdoc`, and a second parse can resurrect markup smuggled through
 * raw-text content (the `<style>`-in-foreign-content shape). Anything that comes back is inert
 * without `allow-scripts`, and a nested frame inherits the sandbox flags. Do not reuse this output
 * anywhere that constraint does not hold.
 *
 * When in a template, don't use this directly, use the `aixSanitizeDocument` pipe instead.
 */
export const sanitizeDocument = (html: string | null | undefined): string => {
    const doc = new DOMParser().parseFromString(html ?? '', 'text/html');

    doc.querySelectorAll(unsafeElements.join(',')).forEach(element => element.remove());

    doc.querySelectorAll('*').forEach(element => {
        Array.from(element.attributes).forEach(({ name, value }) => {
            const attribute = name.toLowerCase();
            const unsafe =
                attribute.startsWith('on') ||
                (urlAttributes.includes(attribute) && !isSafeUrl(value, attribute));
            if (unsafe) {
                element.removeAttribute(name);
            }
        });
    });

    // Links have nowhere to go inside a sandboxed frame; send them to a new tab instead.
    const base = doc.createElement('base');
    base.setAttribute('target', '_blank');
    doc.head.appendChild(base);

    const reset = doc.createElement('style');
    reset.textContent = embedReset;
    doc.head.appendChild(reset);

    return `<!doctype html>${doc.documentElement.outerHTML}`;
};
